import { catalogLeafSkuFromPartId, isCatalogLeafPartId } from '@misumi/shared'
import { useId, useState } from 'react'
import type { FormEvent, MouseEvent } from 'react'
import { DeleteOutlined, DownOutlined, SearchOutlined } from '../../../components/icons'
import { Chip } from '../../../components/ui/Chip'
import type { ChipTone } from '../../../components/ui/Chip'
import { buildLeafAccessibleName, formatBomLineLabel } from '../staging-bom-accessible-names'
import type {
  FileToResolve,
  IdentifiedPart,
  MatchedPart,
  SourcingOption,
  StagedPartType,
} from '../staging-types'
import { PART_TYPE_TONE, STAGED_PART_TYPE_LABEL } from '../staging-types'
import { DrawingAssociationControl } from './DrawingAssociationControl'
import { MatchedSourcingPanel } from './MatchedSourcingPanel'
import { PartThumbnail } from './PartThumbnail'

/**
 * UID-497 (T10) scope (a) — the BOM-leaf drawing picker is PDF-ONLY
 * (`accept=".pdf"` in the design, and a 415 from the node route for anything
 * else). Narrower than `ACCEPTED_DRAWING_EXTENSIONS`, which the part-scoped
 * control keeps: that one also admits `.dwg`/`.dxf`.
 */
const PDF_ONLY_DRAWING_EXTENSIONS = ['.pdf'] as const

interface IdentifiedLeafProps {
  readonly kind: 'identified'
  readonly part: IdentifiedPart
  /** UID-538 (T5), superseded by a product-ruled follow-up — the sheet's
   * `Process`/`Material` cells for this BOM line, verbatim, read-only. No
   * longer rendered as text: `ConfigurationStatusCell` only tests whether
   * either is present (`hasConfig`), to know whether "No configuration"
   * belongs in the Configuration status cell alongside "No drawing" — see
   * `ConfigurationStatus`'s own doc comment and ADR-0149 Am.8 D49. A BOM
   * leaf has no `staged_file` row to `PATCH` a configuration onto either
   * way (ADR-0136 D3). */
  readonly statedProcess?: string | null
  readonly statedMaterial?: string | null
  readonly candidateDrawings: readonly FileToResolve[]
  readonly onAssociateDrawing: (fileId: string) => void
  readonly onGenerateDrawing: () => Promise<unknown>
  readonly onUploadDrawing: (file: File) => Promise<unknown>
  /**
   * ADR-0156 D4 — MUST resolve to `true` on a successful commit and `false`
   * on a rejected one (never throw — `StagingPage`'s `runMutation` already
   * surfaces the error to the page-level alert; this boolean is only how
   * `QuantityInput` knows whether to revert its own draft). See
   * `QuantityInput`'s own doc comment for why a fire-and-forget `void`
   * callback cannot support D4's "revert on a rejected edit" requirement.
   */
  readonly onQuantityChange: (quantity: number) => Promise<boolean>
  readonly onMaterialChange: (material: string | null) => void
  readonly onRemove: () => void
}

interface MatchedLeafProps {
  readonly kind: 'matched'
  readonly part: MatchedPart
  /** UID-538 (T5) — the wire node's OWN `statedPartType`, threaded down from
   * `BomTreeLeafBase` (same field `UnresolvedLeafProps` already carries).
   * Preferred over this arm's own unconditional `'Buy'` fallback for the
   * Part Type chip when present; ADR-0149 Am.1 D16/D18 still forbids folding
   * it INTO `MatchedPart` itself. The IDENTIFIED arm's chip is deliberately
   * NOT given this treatment — `part.type` already varies there, and
   * ADR-0149's OQ-6 (identified leaf whose `statedPartType` DISAGREES with
   * `part.type`) is an open product/PM question this ticket does not answer;
   * only the matched arm's literal `'Buy'` hardcode was in scope (AC). */
  readonly statedPartType?: StagedPartType | null
  readonly expanded: boolean
  readonly onToggleExpand: () => void
  readonly onIgnore: () => void
  readonly onUndo: () => void
  readonly onSelectOption: (optionId: string) => void
  readonly onKeepUploaded: () => void
  /**
   * ADR-0146 D11 step 5 — the catalog-leaf sourcing dropdown's OWN write
   * callback, distinct from `onSelectOption` above. `onSelectOption` flows
   * through `service.swapSourcing` (`partId`-scoped, writes
   * `intake.staged_file_sourcing_option`), which 404s for a catalog leaf —
   * it has no `staged_file` row (D3). This one flows through the new
   * NODE-subject route (`POST .../bom-nodes/:nodeId/sourcing`,
   * `action: 'select'`) instead. Always called with just the `optionId`;
   * the row's stable `nodeId` is curried in by `StagingBomPartsTree`, the
   * same pattern `onSelectOption`/`onToggleExpand` already use.
   */
  readonly onSelectCatalogOption: (optionId: string) => void
  /**
   * ADR-0162 D5 (UID-496) — the catalog leaf's OWN Keep verb, node-subject.
   * `onKeepUploaded` above stays the existing `partId`-scoped path a
   * file-backed matched leaf uses; a catalog leaf has no `staged_file` row for
   * it, so its Keep flows through `POST .../bom-nodes/:nodeId/sourcing` with
   * `action: 'keep'` (the durable `'kept-bom-reference'` arm of migration
   * 1014). Curried with this row's stable `nodeId` by `StagingBomPartsTree`,
   * the same pattern `onSelectCatalogOption` above and `onRemoveCatalogNode`
   * below already use.
   *
   * WHY THIS EXISTS AT ALL, when ADR-0146 D4 ruled the Keep row off this arm:
   * D4's stated premise — no design state and no approved copy for a catalog
   * leaf's Keep — is FALSIFIED by the authoritative design. `stdRow`
   * (`Fictiv Platform.dc.html:9384`) sets `onKeep`/`keepSel`/`kept`, and the
   * "Keep my uploaded part" row (`:3242`) renders inside the `bomRows` loop
   * (`:3165`) — on exactly this population. The design's Keep IS this verb's
   * transient form (`revertMatch`, `:6258`: clear the selection, then toast).
   * D4's NARROWER claim survives and was checked: `stdRow` sets no
   * `cadFile`/`hasCad`, so a catalog leaf carries no CAD FILE. That supports
   * "no CAD file"; it never supported "no design state for Keep".
   */
  readonly onKeepCatalogNode: () => void
  /**
   * ADR-0162 D3.2 (UID-496) — the catalog leaf's OWN Ignore / Undo, node-subject.
   *
   * `onIgnore`/`onUndo` above are `partId`-scoped: `ignoreMatch` writes
   * `staged_file.match_status` and `confirmMatch` un-ignores, neither of which
   * a catalog leaf has a row for. These flow through the SAME node route's
   * `ignore`/`unignore` arms, writing `staged_bom_node.match_ignored_at`
   * (migration 1021).
   *
   * REVERSES ADR-0157 D1.3's *"Ignore / Undo stay ABSENT"* — which was never a
   * UX ruling. D1.3 kept them absent because their subject's durable home did
   * not exist yet; 1021 builds it. The design wanted them all along
   * (`stdRow` sets `onIgnore` at `:9425`, and `ignoreMatch` at `:6259` is a
   * plain toggle), so this is storage catching up with design intent.
   *
   * The toggle is LOSSLESS: `unignore` restores nothing because `ignore`
   * destroyed nothing — the human's sourcing decision lives in a different
   * table and neither arm touches it (ADR-0162 D3.3(1)).
   */
  readonly onIgnoreCatalogNode: () => void
  readonly onUndoIgnoreCatalogNode: () => void
  readonly onRemove: () => void
  /**
   * ADR-0157 D1/D2 — the catalog leaf's OWN Remove verb. `onRemove` above
   * stays the existing part-scoped `DELETE /staging/parts/:partId` used by
   * an identified leaf and a real file-backed matched leaf; a catalog leaf
   * has no `staged_file` row for that route to act on (D3, unchanged), so
   * its trash calls this NODE-scoped verb instead, keyed by this row's own
   * stable `nodeId` (curried in by `StagingBomPartsTree`, the identical
   * pattern `onSelectCatalogOption` above already uses). Never called for a
   * real matched leaf — `StagingBomLeafRow` picks between the two by the
   * SAME `isCatalogLeaf` discriminant the sourcing-dropdown dispatch above
   * already uses.
   */
  readonly onRemoveCatalogNode: () => void
  /**
   * ADR-0156 D1 — the CAPTURED per-node quantity (`StagingBomNode.quantity`),
   * threaded through the view model regardless of `partKind` (real
   * matched-file leaf OR catalog leaf — both write through the SAME
   * node-scoped verb, D1's table). Rendered as the Qty input's PLACEHOLDER,
   * never its value (R5) — `null` renders no placeholder at all.
   */
  readonly capturedQuantity: number | null
  /**
   * ADR-0156 D1/D5(4) — the WORKING quantity a human has typed
   * (`StagingBomNode.desiredQuantity`). `null` = never edited, which is what
   * keeps this leaf's Qty cell reading as "off your sheet, not ours yet"
   * (R5) until `onNodeQuantityChange` fires.
   */
  readonly desiredQuantity: number | null
  /**
   * ADR-0156 D1 — the node-scoped quantity verb's own callback, keyed by
   * this row's stable `nodeId` (curried in by `StagingBomPartsTree`, D1's
   * table: matched/catalog both write `staged_bom_node.desired_quantity`).
   * NEVER the SKU-derived `partId` (ADR-0146 D7's identity hazard — a
   * catalog leaf's `partId` churns on every sourcing selection).
   *
   * ADR-0156 D4 — same `true`/`false`-resolving, never-throwing contract as
   * `IdentifiedLeafProps.onQuantityChange` above.
   */
  readonly onNodeQuantityChange: (quantity: number) => Promise<boolean>
}

interface UnresolvedLeafProps {
  readonly kind: 'unresolved'
  readonly name: string
  /** ADR-0149 Am.1 D19 — the customer's own assertion about THIS BOM line,
   * carried from the wire node through `walk()` (Am.1 D17) regardless of
   * whether `partId` resolved. `null`/absent → variant 4: the row renders
   * byte-identical to before this ADR (D19(4)/D8.3). */
  readonly statedPartType: StagedPartType | null
  /** ADR-0149 Am.1 D19 — this BOM line's `Material` cell, verbatim.
   * STALE-DOC CORRECTION (UID-539 Am.9 D50) — this used to render as
   * read-only text in the meta line (D19(3)), then fed a since-removed
   * material picker (UID-497). Neither survives: the comp renders it
   * nowhere on this row. Its only remaining consumer is the Configuration
   * status cell's `hasConfig` check below. */
  readonly statedMaterial: string | null
  /** UID-536 (T3) — the sheet's `Process` cell, verbatim.
   * STALE-DOC CORRECTION (UID-539 Am.9 D50) — this used to render in the
   * meta line, replacing a hardcoded `Custom` fallback. That render is
   * REMOVED: the comp renders no process/material text on this row at all.
   * Its only remaining consumer is the Configuration status cell's
   * `hasConfig` check below. */
  readonly statedProcess: string | null
  /**
   * UID-517 item 4 (follow-up gap close) — a REAL, cross-session Parts
   * Library thumbnail reused for this leaf, carried through from the wire
   * node on the SAME unconditional terms as `statedPartType`/
   * `statedMaterial` above. `null` means no cross-session match (or none
   * attempted) — the row falls back to the shared decorative placeholder,
   * same as any other genuine miss (never fabricated).
   */
  readonly crossSessionThumbnailUrl: string | null
  /**
   * This BOM line's `Quantity` cell, verbatim — the THIRD customer assertion
   * on the same line, alongside `statedPartType` and `statedMaterial` above,
   * and carried on the same terms (ADR-0149 Am.1 D19 extended).
   *
   * The wire node has always carried it (`bomHierarchy[].quantity`) and
   * `staging-bom-tree-view-model.ts` threads it through as
   * `quantity: number | null`, but the row dropped it, so a quantity the
   * customer stated in their own spreadsheet rendered as an empty cell.
   *
   * STALE-DOC CORRECTION (Gate-5 W5) — this field is **NO LONGER READ-ONLY
   * TEXT** and this row **NO LONGER SKIPS `QuantityInput`**. ADR-0156 D1/R5
   * SUPERSEDES that: this value is now the input's PLACEHOLDER (see
   * `desiredQuantity` below and the `QuantityInput` it renders), and
   * `sumGroupQuantity()` NO LONGER contributes a flat 0 for this leaf once a
   * human types a `desiredQuantity` (D2's EXTENSION) — an assembly `Qty = N`
   * pill DOES change once this leaf is edited. Both of the OLD claims below
   * were true pre-ADR-0156 and are false now; kept struck through, not
   * deleted, so the next implementer sees exactly what changed and why,
   * rather than re-discovering the same "it's read-only" assumption that
   * would re-break the captured/desired split (F2).
   *
   * `null`/absent → the Qty cell's PLACEHOLDER is empty, byte-identical to
   * before (D19(4)/D8.3's fallback contract) UNTIL a `desiredQuantity` is
   * typed.
   *
   * A stated quantity alone (no `desiredQuantity` yet) is still a claim
   * about one line, not a total this app can stand behind — `desiredQuantity
   * ?? 0` (never `statedQuantity`) is what `sumGroupQuantity()` reads.
   */
  readonly statedQuantity?: number | null
  /**
   * ADR-0156 D1/D5(4) — the WORKING quantity a human has typed for this BOM
   * line (`StagingBomNode.desiredQuantity`). An unresolved leaf HAS a node
   * (it is the node the leaf's own `nodeId` names — unlike an identified
   * leaf, it just has no `partId` resolution), so it takes the SAME
   * node-scoped verb the matched/catalog arms use. `null` = never edited.
   */
  readonly desiredQuantity?: number | null
  /** ADR-0156 D1 — same node-scoped verb the matched/catalog arms use, see
   * `MatchedLeafProps.onNodeQuantityChange`'s doc comment (including its
   * D4 `true`/`false`-resolving contract). */
  readonly onNodeQuantityChange: (quantity: number) => Promise<boolean>
  /**
   * Gate-5 re-review residual — this leaf's own 0-based position among its
   * direct siblings (`BomTreeLeafNode.siblingIndex`, see that field's doc
   * comment in `staging-bom-tree-view-model.ts`). `bomLine` is `null` for
   * EVERY leaf on a customer sheet with no recognized line-number column
   * (`parseNonNegInt('')` → `null`), which otherwise drops the Qty-input/
   * checkbox accessible-name disambiguator entirely (two same-named
   * unresolved siblings collide) or, worse, collapses a blank-named leaf's
   * whole `displayName` to the bare, non-unique string `"BOM line"`. This
   * is the fallback ONLY — a real `bomLine` is always preferred (it is the
   * customer's own reference and more meaningful than an ordinal).
   */
  readonly siblingIndex: number
  /**
   * ADR-0157 D3 — `null` = live, non-null = dismissed. The row NEVER
   * vanishes when dismissed (D3.1's soft-dismiss ruling): the quantity
   * capture stays visible/announced and focus management stays trivial
   * (Undo occupies the exact cell Dismiss did). Drives the Dismiss/Undo
   * button swap, the "Dismissed" chip, and disabling (never hiding)
   * `QuantityInput`.
   */
  readonly dismissedAt: string | null
  /** ADR-0157 D3/D2 — dismisses THIS BOM line (soft — the same node-scoped
   * verb the catalog leaf's hard Remove uses, `MatchedLeafProps.
   * onRemoveCatalogNode`'s doc comment; the server decides soft-vs-hard by
   * arm). Reversible — never a `ConfirmDialog` (product-designer's spec,
   * house rule: per-row actions fire immediately; a confirm gate is for
   * coarse-grained, non-reversible destruction only). */
  readonly onDismiss: () => void
  /** ADR-0157 D3.7 — reverses a dismissal. The data has always been
   * reversible (`dismissed_at` is a nullable column); this is the UI
   * surfacing that reversibility, per the product-designer's spec
   * authorizing it for this row kind (reusing the SAME Ignore/Undo pattern
   * already shipped on the matched arm — see `MatchedCatalogMatchCell`
   * below). */
  readonly onUndoDismiss: () => void
  /**
   * UID-497 (T10) — the CUSTOM ("Make") leaf's own drawing + material controls.
   *
   * REQUIRED, not optional-with-default, for the same reason `occurrenceCount`
   * is (see its doc comment): a missed hand-off from `StagingBomPartsTree` must
   * fail `tsc`, not silently render a Make row with no way to attach the
   * drawing it needs. They are threaded for EVERY unresolved leaf and the ROW
   * decides whether to render them — only `statedPartType === 'custom'` gets
   * these controls, and a catalog leaf gets none of them (scope (f): the comp's
   * `stdRow` has no drawing or material control at all).
   *
   * ## Why they are here rather than on the identified arm's props
   *
   * `IdentifiedLeafProps` already declares the same five callbacks, and this
   * story's whole premise is that they are UNREACHABLE for a custom BOM line:
   * that arm renders only when a `staged_file` row backs the leaf, and a custom
   * line has none. These are the NODE-scoped counterparts — the same
   * distinction `onNodeQuantityChange` above draws against `onQuantityChange`.
   */
  readonly candidateDrawings: readonly FileToResolve[]
  /** The drawing currently attached to THIS BOM line, or `null` for none (the
   * `drawMissing` state). */
  readonly drawingFilename: string | null
  /** UID-497 — associate a drawing from the session pool, BY FILENAME. Not
   * `fileId`: the pool is not just files-to-resolve rows (it also holds the
   * parts' own drawings and other leaves' generated ones), and the server
   * re-resolves the name against that pool, so nothing arbitrary can attach. */
  readonly onAssociateDrawing: (filename: string) => void
  readonly onDetachDrawing: () => void
  /** Resolves when the drawing has been attached. The ~1.9s demo delay lives at
   * the `StagingPage` wiring (one named constant), not here — this component
   * only needs to know when it is done. */
  readonly onGenerateDrawing: () => Promise<unknown>
  readonly onUploadDrawing: (file: File) => Promise<unknown>
  /** The material a HUMAN CHOSE on this line, or `null`. Never conflated with
   * `statedMaterial` above (the customer's own cell). Unreachable from this
   * row's own UI since UID-539 Am.9 D50 removed its picker; kept for a prior
   * selection to still feed `hasConfig` below. */
  readonly selectedMaterial: string | null
  /** `null` clears the selection back to the stated material. Unreachable
   * from this row's own UI for the same reason as `selectedMaterial` above —
   * the write path itself is intentionally left intact, not deleted. */
  readonly onMaterialChange: (material: string | null) => void
}

export type StagingBomLeafRowProps = {
  readonly nodeId: string
  readonly bomLine: number | null
  /**
   * UID-525 / ADR-0168 — CURRENTLY UNREAD BY THIS COMPONENT, deliberately.
   *
   * A part row's leading inset is now a flat 80px at every depth, because
   * that is what both of the comp's row builders hard-code (`dc.html:9441`,
   * `:9442`) — so nothing here varies with `depth` any more, and it is no
   * longer destructured.
   *
   * Kept on the interface rather than deleted because ADR-0168 leaves the
   * per-leaf depth affordance explicitly OPEN: it was reversed because the
   * mechanism (a rail inside the Part cell) put names 108px off their
   * header, not because per-leaf depth was ruled out on design grounds. If
   * design asks for it back, this is the seam it returns through, and the
   * caller already computes the value for the group rows. Costs the caller
   * nothing today; a re-add would otherwise have to re-thread it through
   * `BomTreeRows`.
   */
  readonly depth: number
  readonly selected: boolean
  /** ADR-0142 D7(1) — takes the checkbox's own CLICK event (not `onChange`)
   * so `event.shiftKey` can thread through to the unified list's
   * shift-click range selection: React's `ChangeEvent` carries no
   * modifier-key fields at all (verified — TS2339 on `.shiftKey`), so only
   * `onClick`'s `MouseEvent` can supply it. Matches the established
   * pattern already shipped on `IdentifiedPartRow`'s checkbox
   * (`onClick={onToggleSelect}` + a no-op `onChange` to satisfy React's
   * controlled-input requirement) — not a new pattern. */
  readonly onToggleSelect: (event: MouseEvent<HTMLInputElement>) => void
  /** ADR-0136 R1 — the chain of ancestor assembly names, immediate parent
   * first, outward (e.g. `['Spindle assembly', 'Control Assembly']`). Used
   * ONLY to disambiguate this row's checkbox/Remove accessible names from
   * the flat bucket row's identical `Select {name}`/`Remove {name}` labels
   * once the tree is expanded (the bucket's own labels stay unqualified —
   * they are the canonical copy). Empty for a leaf with no assembly
   * ancestor. */
  readonly ancestorNames: readonly string[]
  /** UX-spec §9.5(b) (Gate-5 fix) — true only for the `ungrouped` coverage-
   * diff population (an identified/matched part no `bomHierarchy` leaf
   * references at all). Distinguishes it from a native loose BOM line
   * (`assembly: null`, `bomLine` possibly present) which already carries its
   * own `Line N` and needs no change. When true and `bomLine` is null (the
   * only combination this population can produce), renders a factual marker
   * in place of `Line N` instead of leaving the meta line blank. */
  readonly isCoverageGap: boolean
  /** UX-spec §9.7/§9.8.3 — how many `bomHierarchy` leaves cited this same
   * `partId` before dedupe collapsed them to this one surviving row.
   * `1` for no duplicate, or for an unresolved leaf (never deduped, D17.1).
   * REQUIRED, not optional-with-default: this field's entire purpose is
   * honesty about multiplicity, so a missed hand-off from the tree-building
   * layer must fail `tsc`, not silently degrade to `1`. Every call site
   * (including the unresolved one, where it is always `1`) passes it
   * explicitly. */
  readonly occurrenceCount: number
} & (IdentifiedLeafProps | MatchedLeafProps | UnresolvedLeafProps)

/**
 * A single BOM-tree leaf ("part") row — ADR-0086 D1's frozen contract, laid
 * out per `docs/design/staging-bom-hierarchy-tree-ux-spec.md`
 * §3.4/§4.1/§4.2/§4.3.
 *
 * CITATION CORRECTION (ADR-0144 D5 / Finding A): those §-numbers were
 * previously attributed to "ADR-0086 §3.4/§4.1/§4.2/§4.3". ADR-0086 has only
 * D1–D4 and no §-numbered sections at all — the §-numbers belong to the
 * product-designer's UX SPEC. Naming the wrong authority is how a designer's
 * still-open question (§OQ-5) hardened into an "ADR clause" that could not be
 * questioned; ADR-0144 D5 fixes all four sites.
 *
 * Reuses the SAME per-part action wiring `IdentifiedPartRow`/`MatchedPartRow`
 * already use (drawing associate/upload/generate, qty, material, sourcing
 * swap, ignore/undo, remove) via the identical callback props threaded from
 * `StagingPage.tsx`'s `service.*`/`runMutation` calls — no new mutation seam.
 * The DRAWING / catalog-match cell is a single merged `DropdownMenu`-triggered
 * control per UX-spec §4.1 ("not a new component per state"), replacing
 * `IdentifiedPartRow`'s separate drawing-only trigger and `MatchedPartRow`'s
 * separate "Swap" button+table with one column that covers both.
 *
 * REMOVE (ADR-0144 D1/D3): the trailing Remove button is the BOM-line delete,
 * and it is a COMPOSITE server verb — `DELETE /staging/parts/:partId` removes
 * the staged part AND drops every tree leaf citing it (see
 * `apps/api/src/modules/parts-library/remove-bom-leaf.ts` for why removing
 * only the part RELOCATES this row to the ungrouped tail instead of deleting
 * it). LEAF-ONLY and RESOLVED-ONLY: the `unresolved` branch below offers no
 * Remove (dismissing a diagnostic row is a different verb, deferred to R1),
 * and assembly rows never have one.
 */
export function StagingBomLeafRow(props: StagingBomLeafRowProps) {
  const {
    nodeId,
    bomLine,
    selected,
    onToggleSelect,
    ancestorNames,
    isCoverageGap,
    occurrenceCount,
  } = props
  // ADR-0146 D8 — the disclosure trigger (`MatchedCatalogMatchCell`) and its
  // panel (`MatchedExpandedRow`) are siblings, not parent/child (a `<tr>`
  // cannot nest inside another `<tr>` — see `MatchedExpandedRow`'s own
  // comment), so the `aria-controls`/`id` pair they share is minted ONCE
  // here and threaded to both, rather than each generating its own id that
  // could never match the other's. Called unconditionally, before any
  // early return, per the Rules of Hooks.
  const panelId = useId()

  if (props.kind === 'unresolved') {
    // ADR-0136 §4 — a BOM line whose `partId` doesn't resolve against
    // identified[]/matched[]: the CSV named a part this session's uploaded
    // files never produced (not a part that "vanished" — there's no instant
    // within one atomic `getStagingTriage` read where that could be true).
    // Rendered as an ordinary-shaped row with nothing to select/remove,
    // never a full-row alert sentence — this is diagnostic, not urgent, and
    // for a large share of real BOMs (a stocked fastener, an un-reuploaded
    // sub-part) it is simply normal.
    // Gate-5 RE-REVIEW residual — `bomLine` is `null` on EVERY leaf when the
    // customer sheet has no recognized line-number column at all
    // (`parseNonNegInt('')` → `null`, `bom-spreadsheet-parser.ts:507-517`),
    // which is a WHOLE-SHEET condition, not a per-row rarity. `bomLineLabel`
    // is the ONE place that fact is resolved to a human-readable, ALWAYS
    // present label: the customer's own `bomLine` when it exists (preferred
    // — it's their own reference), else this leaf's own `siblingIndex`
    // (0-based position among its direct siblings, ALWAYS present by
    // construction) rendered as a 1-based ordinal. Never the bare string
    // "BOM line" with nothing after it — that string, alone, is what made
    // every blank-named, `bomLine === null` row announce the IDENTICAL
    // "Quantity for BOM line" to a screen-reader user.
    const bomLineLabel = formatBomLineLabel(bomLine, props.siblingIndex)
    const displayName = props.name.trim() || bomLineLabel
    // ADR-0149 Am.1 D19 — `statedPartType` is a claim about what the
    // customer's OWN LINE says, never a resolution state (D5): it is
    // orthogonal to `partKind === null` and must not be read as a match.
    // `null`/absent is variant 4 — no chip, no placeholder, byte-identical
    // to the pre-ADR row (D19(4)/D8.3).
    const statedPartType = props.statedPartType
    const statedQuantity = props.statedQuantity ?? null
    // Gate-5 fix (code-review C1), RE-FIXED at Gate-5 re-review — ADR-0156
    // D3/D17.1: the unresolved arm is the ONE leaf kind NEVER deduped by
    // `dedupeByPartId`, so two same-named siblings under the same parent
    // (e.g. two "M3x10 screw" lines) are EXPECTED, not exceptional — and
    // `buildLeafAccessibleName`'s only disambiguator is the ancestor chain,
    // which is identical for both. Before ADR-0156 this cell was a
    // read-only `<span>` with no WCAG 4.1.2 "Name" obligation; it is now an
    // editable text field (D6 forbids exactly the "N identical Quantity
    // controls" failure this would otherwise be). `bomLineLabel` (real
    // `bomLine` OR the `siblingIndex` fallback — never conditioned on
    // `bomLine !== null` alone, which is the C1-regression bug this
    // re-review caught) disambiguates it, matching the blank-name
    // fallback's own voice above — but only appended when `displayName` is
    // the real customer name (the blank-name fallback already embeds
    // `bomLineLabel` once, so appending it again would be redundant, not
    // more unique).
    const accessibleLeafName =
      displayName === props.name.trim() ? `${displayName}, ${bomLineLabel}` : displayName
    // ADR-0157 D3 — the row NEVER vanishes when dismissed; this flag drives
    // the chip swap, the checkbox's disabled-label copy, disabling (never
    // hiding) `QuantityInput`, and the Dismiss/Undo button swap.
    const isDismissed = props.dismissedAt !== null
    // Human ruling (Part 2 spec) — reuses `buildLeafAccessibleName` exactly
    // as the resolved arm's checkbox/Remove labels do, so "Dismiss M3x10
    // screw, BOM line 14" / "Undo dismissing M3x10 screw, BOM line 14" fall
    // out of the SAME template rather than a third inline string.
    const dismissLabel = buildLeafAccessibleName('Dismiss', accessibleLeafName, ancestorNames, 1)
    const undoDismissLabel = buildLeafAccessibleName(
      'Undo dismissing',
      accessibleLeafName,
      ancestorNames,
      1,
    )
    return (
      <tr
        id={nodeId}
        className="staging-row staging-bom-row staging-bom-row-unresolved"
        data-node-id={nodeId}
      >
        <td>
          {/* UX-spec §9.3 (Gate-5 fix) — see the group checkbox's identical
              comment: a 24×24 `<label>` extends the click/tap target without
              resizing the visible (disabled) glyph. */}
          <label className="staging-bom-checkbox-target">
            <input
              type="checkbox"
              /* UX-spec §9.8.2 item 3 (Finding 4) — thread the SAME leaf
                 suffix (full ancestor chain) used by the resolved branch
                 below, so this row also states its own position in the
                 hierarchy. `occurrenceCount` fixed at `1`: an unresolved
                 leaf is never deduped (D17.1), so the multiplicity clause
                 never applies here. */
              aria-label={buildLeafAccessibleName(
                // Human ruling (Part 2 spec) — the disabled checkbox's own
                // label switches from "Nothing staged for…" to "Dismissed:
                // …" once dismissed, so a screen-reader user hears the
                // row's current state, not a stale "nothing staged" claim
                // about a line that was in fact staged and withdrawn.
                isDismissed ? 'Dismissed:' : 'Nothing staged for',
                accessibleLeafName,
                ancestorNames,
                1,
              )}
              checked={false}
              disabled
              onChange={() => {}}
            />
          </label>
        </td>
        <td>
          {/* UID-517 item 4 (follow-up gap close) — an unresolved leaf now
              renders a REAL, reused Library photo when one exists
              (`props.crossSessionThumbnailUrl`), instead of always the
              decorative placeholder. This is the ONLY place that field is
              consumed — a resolved leaf keeps reading `part.thumbnailUrl`
              below, never this one. */}
          <PartThumbnail className="staging-row-thumb" url={props.crossSessionThumbnailUrl} />
        </td>
        <td>
          {/* UID-525 / ADR-0168 — no rail, and no per-depth inset. Every
              part row carries the comp's FLAT 80px leading inset on its
              first cell (`.staging-row.staging-bom-row > td:first-child`),
              which both of the comp's row builders hard-code regardless of
              nesting (`dc.html:9441`, `:9442`). This cell therefore starts
              at the Part column's own edge and the name lines up under its
              header — as a rail inside this cell, the indent was added
              AFTER the checkbox and thumbnail columns rather than
              subsuming them, putting names 108px off their header. */}
          <div className="staging-bom-name-cell staging-bom-leaf-name-cell">
            <div className="parts-row-name">
              <span className="parts-row-name-text" title={displayName}>
                {displayName}
              </span>
              <span className="caption-text staging-bom-leaf-meta">
                {/* Defect fix — the coverage-gap sentence ("Not referenced in
                  this BOM's structure") is REMOVED per product ruling: it is
                  not wanted in the app. `bomLine === null` now simply renders
                  nothing here; `isCoverageGap` itself is untouched — it still
                  drives `isUnactionableCatalogRow` (see that const's doc
                  comment). The `·` separator below is gated on `bomLine !==
                  null` only (no longer `|| isCoverageGap`), since there is no
                  longer a coverage-gap fragment for it to follow. */}
                {/* UID-539 QA-gate defect fix (live-review follow-up) — the
                  comp renders NEITHER an interactive material control NOR
                  plain process/material text anywhere on this row (see the
                  ADR-0149 Am.9 D50 comp-verification: `custMeta`,
                  `dc.html:9441`, and `matMenuOpen`/`matOptions`/`onSetMat`,
                  `:9296-9308`, are both computed but read by no template
                  location on this screen). The process label + material text
                  this span used to carry (UID-536/UID-497, ADR-0149 Am.4
                  D45) are REMOVED entirely; the meta line is `Line N` alone,
                  matching the comp. */}
                {bomLine !== null && <span>Line {bomLine}</span>}
              </span>
            </div>
          </div>
        </td>
        {statedPartType === 'catalog' || statedPartType === 'custom' ? (
          <>
            {/* ADR-0149 Am.1 D19(2)/(3) — the single `colSpan={3}` cell is
                un-collapsed into the SAME three cells the header row
                declares (WCAG 1.3.1 — cell count must stay coherent with
                `<thead>` across every variant), so this branch adds no new
                column and removes none. */}
            <td className="staging-bom-drawing-cell">
              {/* UID-497 (T10) scope (a)/(f) — the DRAWING column. A CUSTOM
                  line gets the drawing control here, exactly where the comp
                  puts it (`dc.html:3189`'s `isCustom` branch); a CATALOG line
                  gets none at all (scope (f): `stdRow` has no drawing control,
                  so this is not "the same cell, disabled" — there is nothing
                  to open) and keeps the true, unchanged "not included" chip.
                  A DISMISSED line keeps its chip too, whatever it states:
                  offering a drawing control on a withdrawn line would invite
                  work on a row that is not participating.
                  D7 still holds — a stated type is NOT a catalog match, and
                  nothing here claims one. */}
              {statedPartType === 'custom' && !isDismissed ? (
                <DrawingAssociationControl
                  partName={accessibleLeafName}
                  drawingFilename={props.drawingFilename}
                  candidateDrawings={props.candidateDrawings}
                  // The pool is named by FILENAME on this arm (see the prop's
                  // own comment), so the control's `fileId` callback is mapped
                  // back to the file's name here rather than at the page.
                  onAssociateDrawing={(fileId) => {
                    const file = props.candidateDrawings.find((f) => f.fileId === fileId)
                    if (file !== undefined) {
                      props.onAssociateDrawing(file.filename)
                    }
                  }}
                  onDetachDrawing={props.onDetachDrawing}
                  onGenerateDrawing={props.onGenerateDrawing}
                  onUploadDrawing={props.onUploadDrawing}
                  // `accept=".pdf"` — the design's own restriction, and the
                  // node route 415s anything else.
                  acceptExtensions={PDF_ONLY_DRAWING_EXTENSIONS}
                  // Design-parity fix — a Make part with no drawing renders
                  // the SAME plain neutral dashed "No drawing" affordance a
                  // BOUND Make row with no PDF match uses (UID-402's
                  // unconditional rule: "orange on Triage means Needs
                  // resolution"). `warnWhenEmpty` (the amber-border "needs
                  // attention" opt-in ADR-0149 Am.1 D19/UID-497 T10 scope (c)
                  // previously turned on here) is deliberately NOT passed —
                  // this arm no longer has any caller.
                  emptyLabel="No drawing"
                  menuLabel={`Associated drawing for ${accessibleLeafName}`}
                  // UID-560 — this column ("Catalog match / drawing") wants a
                  // consistent pill width per row, not shrink-to-fit; the
                  // column already has a fixed width (`.staging-bom-col-match`).
                  fullWidth
                />
              ) : isDismissed ? (
                <Chip tone="warning">Dismissed</Chip>
              ) : (
                <Chip tone="neutral">Not included in this upload</Chip>
              )}
            </td>
            <td>
              <Chip tone={PART_TYPE_TONE[statedPartType]}>
                {STAGED_PART_TYPE_LABEL[statedPartType]}
              </Chip>
            </td>
            {/* Product-ruled follow-up (post-UID-538) — the SAME
                `ConfigurationStatus` the identified arm's cell uses below,
                naming whichever of "stated Process/Material" and "drawing
                attached" is missing, blank once both are present. Replaces
                this cell's previous "Not included in this upload" `Chip`. */}
            <td className="staging-bom-status-cell">
              {statedPartType === 'custom' && !isDismissed && (
                <ConfigurationStatus
                  hasConfig={[props.statedProcess, props.statedMaterial].filter(Boolean).length > 0}
                  hasDrawing={Boolean(props.drawingFilename)}
                />
              )}
            </td>
          </>
        ) : (
          <td colSpan={3}>
            {isDismissed ? (
              <Chip tone="warning">Dismissed</Chip>
            ) : (
              <Chip tone="neutral">Not included in this upload</Chip>
            )}
          </td>
        )}
        {/* ADR-0156 D1/R5 — the canonical design puts an editable Qty input
            on EVERY leaf row, unresolved included: an unresolved leaf HAS a
            node (`nodeId`), just no `partId` resolution, so it takes the
            SAME node-scoped verb the matched/catalog arms use
            (`onNodeQuantityChange`). The stated/captured value renders as
            the input's PLACEHOLDER, never its filled value (R5) — reads as
            "off your sheet, not ours yet" until edited. Deliberately NOT
            rolled into `sumGroupQuantity()`'s pill while un-edited — see
            that function's own comment. */}
        {/* UID-525 / ADR-0168 — the comp's `flex:1` spacer column
            (`dc.html:3161`), which pushes Qty and the delete action to the
            trailing edge and absorbs the table's slack. */}
        <td />
        <td className="staging-bom-qty-cell">
          <QuantityInput
            value={props.desiredQuantity ?? null}
            placeholder={statedQuantity}
            onQuantityChange={props.onNodeQuantityChange}
            ariaLabel={buildLeafAccessibleName(
              'Quantity for',
              accessibleLeafName,
              ancestorNames,
              1,
            )}
            showCapturedAffordance
            // ADR-0157 D3 — DISABLED, never hidden, once dismissed: the
            // captured placeholder and its sr-only "Captured from the
            // uploaded file…" description both keep rendering (this is the
            // one property that makes a dismissed line's stated quantity
            // still readable/announced — the whole reason D3.1 ruled soft
            // over hard delete).
            disabled={isDismissed}
          />
        </td>
        <td className="staging-bom-remove-cell">
          {isDismissed ? (
            <button
              type="button"
              // Human ruling (Part 2 spec) — reuses the SAME shipped
              // Ignore/Undo shell (`MatchedCatalogMatchCell`'s
              // `staging-drawing-trigger staging-drawing-trigger-none`),
              // never a parallel pattern, plus the scoped AA-contrast
              // override (`uploads.css`).
              className="staging-drawing-trigger staging-drawing-trigger-none staging-bom-dismiss-action"
              aria-label={undoDismissLabel}
              onClick={props.onUndoDismiss}
            >
              Undo
            </button>
          ) : (
            <>
              {/* Human instruction (2026-09-06) — the dismiss control is the
                design's `data-icon="delete"` glyph, not a text button:
                COSMETIC ONLY. `DeleteOutlined` renders exactly that
                attribute (`@ant-design/icons`' IconBase stamps
                `data-icon: <name>`, and this asset's name is "delete"), and
                it is the SAME component the sibling leaf rows' trash uses
                (`staging-row-remove` above), so the two read as one control
                family. Behaviour is untouched: same `onClick`, same
                `aria-label` — the accessible name still carries the verb
                ("Dismiss <name>, BOM line N"), which is what keeps this a
                DISMISS and not a delete for AT users even though the glyph
                is shared. Undo stays a TEXT button deliberately: there is no
                unambiguous icon for "undo a dismissal", and it is the
                recovery path. */}
              <button
                type="button"
                className="staging-row-remove staging-bom-dismiss-action"
                aria-label={dismissLabel}
                onClick={props.onDismiss}
              >
                <DeleteOutlined aria-hidden="true" />
              </button>
            </>
          )}
        </td>
      </tr>
    )
  }

  const name = props.part.name
  // UID-493 — the customer's own catalog SKU under the name, matching the
  // comp's `pnLabel` rule (Fictiv Platform.dc.html:9408): absent for an
  // identified/custom leaf (no catalog SKU exists), and hidden for a matched
  // leaf whose SKU is itself a placeholder value.
  const partNumberLabel =
    props.kind === 'matched' ? catalogLeafPartNumberLabel(props.part.partId) : null
  // UID-494 (T7) — the pack heuristic (`Fictiv Platform.dc.html:9390`'s
  // `isPack`), mirrored client-side per the ticket's own fallback ("mirror it
  // exactly" — T2's `generateDemoSourcing` computes `displayName`/
  // `displayQuantity` but neither reaches the wire, see
  // `demo-sourcing-projection.ts`'s `demoMatchedPart`). `capturedQuantity`
  // (the SHEET's own quantity), never `desiredQuantity` — a human's typed
  // override is authoritative and must never be re-collapsed to 1.
  const packDisplay =
    props.kind === 'matched' ? packDisplayFor(props.part.name, props.capturedQuantity) : null
  // UID-494 (T7) code-review C4 fix — scope (d)/AC: an ignored matched leaf's
  // NAME is struck through and de-emphasised (`stdRow`'s `ignoredNameStyle`,
  // `Fictiv Platform.dc.html:9416`: `text-decoration:line-through;
  // color:var(--fg-4)`). This is the NAME treatment, distinct from the
  // "Ignored" state Chip `ConfigurationStatusCell`/`MatchedCatalogMatchCell`
  // already render (that is the state label, not the name).
  const isIgnoredMatchedLeaf = props.kind === 'matched' && props.part.status === 'ignored'
  /**
   * ADR-0145 D1/D5/D7 — is this row on the CATALOG resolution arm?
   *
   * Read off the `partId` namespace (`@misumi/shared`'s `isCatalogLeafPartId`,
   * the single owner of that convention), which is precisely what D1 minted the
   * prefix FOR: `partId` stays a bare string, and the arm is the discriminant
   * the data already carries. No new contract field, no second representation.
   *
   * WHAT IT CHANGES, and why each is ABSENT rather than disabled-or-erroring.
   * A catalog leaf has no `intake.staged_file` row — that is the premise of
   * migration 1013's second arm — and no durable match-state home either
   * (1013's DECISION 2 declined the `staged_bom_node`-keyed sourcing/match
   * table). So on this row:
   *   - the DRAWING verbs have no subject at all (D5: associate-drawing,
   *     generate-drawing, register-uploaded-drawing, keep-uploaded are ABSENT).
   *     Already structurally true here — they live on the `identified` branch —
   *     and `StagingBomLeafRow.catalog-leaf.test.tsx` now asserts it rather
   *     than leaving it as an accident of the branch shape.
   *   - REMOVE is now BUILT here (ADR-0157 D1/D2), not absent. Remove's
   *     SUBJECT is the BOM LINE — never a file — and ADR-0145 D5 remains the
   *     governing decision (Remove: KEEP on a catalog leaf). The prior
   *     version of this comment called Remove a "file-subject MUTATION verb"
   *     citing ADR-0144 D7; ADR-0157 D1 REVERSES that: D7 rules on Ask 1
   *     (catalog match) and Ask 3 (Add to BOM) only and contains no ruling on
   *     Remove at all — a phantom citation, of the exact class ADR-0144's own
   *     R20 named (widened by ADR-0157 R5 to cover `ADR-NNNN D<n>`-shaped
   *     citations, not just `§`-shaped ones). The real, honest reason Remove
   *     stayed unbuilt until now was NEVER a subject mismatch — it was the
   *     plain absence of a node-keyed write path (1013 DECISION 2 declined
   *     one; 1014 built it), recorded at
   *     `staging-service.catalog-leaf.test.ts`. Now that the path exists
   *     (`DELETE /staging/bom-nodes/:nodeId`, `onRemoveCatalogNode`), Remove
   *     renders on this arm exactly like every other leaf's, with no
   *     "catalog" wording added to its accessible name — same control, same
   *     treatment, no distinguishing (ADR-0146 D8's 4.1.2 lesson: a name must
   *     not claim a difference in WHAT the control does that isn't there).
   *   - IGNORE / UNDO stay ABSENT — ADR-0157 D1.3 keeps them there
   *     deliberately: their subject is the MATCH CANDIDATE
   *     (`staged_bom_node_match_state`, migration 1014), ADR-0146's surface,
   *     not this one, and that table still has no durable per-node write path
   *     for a catalog leaf. Still 404 at the store; still not rendered.
   *   - CHECKBOX SELECTION is likewise off: it feeds the bulk-confirm CTA, and
   *     offering a confirm that the server refuses is the one outcome D7 rules
   *     out most explicitly (D5 of ADR-0146 — the confirm refusal stays).
   *   - QUANTITY stays visible and READ-ONLY (D5.1's ruled default), which the
   *     `matched` branch already does: the BOM asserted N and we show N.
   *
   * CORRECTED, ADR-0146 D1/D11 step 5: "Select option" is NO LONGER on this
   * list. `resolveOne` always searched with `limit: 10` and DISCARDED up to
   * nine rows — the candidates were never missing, only thrown away — and a
   * concrete catalog hit does NOT resolve to exactly one option; D1 stops the
   * discard, so `sourcingOptions` carries the exact hit plus its non-exact
   * candidates. D11 step 4 built the durable, NODE-subject write path
   * (`POST .../bom-nodes/:nodeId/sourcing`) that a catalog leaf's selection now
   * uses (`onSelectCatalogOption`, never `onSelectOption`, which still targets
   * the `staged_file`-scoped `/staging/swap` route this leaf has no row for).
   * This is a KNOWN, RECORDED divergence from D5, which rules the OTHER
   * part-subject verbs (Remove/Ignore/Undo) KEEP: they still cannot execute,
   * per the scope-boundary note in
   * `apps/api/src/modules/parts-library/staging-service.catalog-leaf.test.ts`.
   */
  const isCatalogLeaf = props.kind === 'matched' && isCatalogLeafPartId(props.part.partId)
  /**
   * Gate-5 **C-D** — this row is a PERMANENT dead end for every verb the
   * checkbox feeds, so the checkbox is disabled rather than offered.
   *
   * `isCoverageGap` marks the `ungrouped` coverage-diff population (ADR-0142
   * D2(4) / UX-spec §9.5(b)): a `matched[]`/`identified[]` part no hierarchy
   * leaf references. Those leaves are SYNTHESIZED here, with a client-only
   * `nodeId` (`bom-ungrouped-matched-<partId>`) that has no server row behind
   * it. On the CATALOG arm that leaves nothing addressable:
   *
   *  - `removeBomNode` 404s — the id exists only in this browser;
   *  - `removeStagedPart` cannot reach it either, because a catalog leaf has no
   *    `intake.staged_file` row by construction (migration 1013's arm mutex);
   *  - and CONFIRM needs a node too, since ADR-0162 D2.4 records WHICH BOM
   *    position was confirmed in `staged_bom_node.match_confirmed_at`.
   *
   * So every destination is impossible, permanently — not "failed, retry". The
   * bulk delete's copy made that worse by advising a retry that could never
   * succeed, on a row that stayed rendered with its id stuck in the selection.
   * ADR-0144 D7's principle is exactly this: do not present a control whose
   * only outcome is a dead end.
   *
   * The row itself still RENDERS, and must — dropping it is ADR-0086 BUG-1,
   * which ADR-0142 D2(4) exists to prevent. Only the control goes.
   *
   * The FILE-BACKED arm is untouched: an ungrouped file-backed part still has
   * its `intake.staged_file` row, so `removeStagedPart` genuinely works on it.
   * This is a catalog-arm condition, not a coverage-gap one.
   */
  const isUnactionableCatalogRow = isCatalogLeaf && isCoverageGap
  // UX-spec §9.8.2 item 2 (R7, reframed) — `occurrenceCount > 1` inverts
  // which clause is grammatically primary: "referenced at N positions…
  // including X" states X as one true instance of a stated total, rather
  // than "in X" asserting X as the sole containing location. Same facts as
  // §9.7's original copy, reordered per compliance's "stop asserting a
  // single path" finding (§9.8.2 item 2).
  const checkboxLabel = buildLeafAccessibleName('Select', name, ancestorNames, occurrenceCount)
  const removeLabel = buildLeafAccessibleName('Remove', name, ancestorNames, occurrenceCount)
  // ADR-0156 D6/R4 — the SAME accessible-name convention `checkboxLabel`/
  // `removeLabel` already use, so a screen-reader user hears a UNIQUE,
  // row-identifying name on every Qty field rather than N identical
  // "Quantity" controls (the live merge-blocking WCAG 1.3.1/4.1.2 defect
  // this ADR scales, ADR-0146 D8's precedent). The captured-vs-desired
  // split deliberately does NOT appear in the NAME — a name that changes
  // the moment the user types is more churn than 4.1.2 wants; that
  // distinction is carried by `QuantityInput`'s own `aria-describedby`
  // instead (R5).
  const quantityLabel = buildLeafAccessibleName(
    'Quantity for',
    name,
    ancestorNames,
    occurrenceCount,
  )
  // UID-538 (T5) — the matched arm's OWN stated part type wins over its
  // unconditional `'Buy'` fallback (AC). The identified arm keeps reading
  // `part.type` only, byte-identical to before this ticket — see
  // `MatchedLeafProps.statedPartType`'s doc comment for why (ADR-0149 OQ-6).
  const partTypeForChip: StagedPartType =
    props.kind === 'identified' ? props.part.type : (props.statedPartType ?? 'catalog')

  return (
    <>
      <tr id={nodeId} className="staging-row staging-bom-row" data-node-id={nodeId}>
        <td>
          {/* UX-spec §9.3 (Gate-5 fix) — see the group checkbox's identical
              comment. */}
          <label className="staging-bom-checkbox-target">
            <input
              type="checkbox"
              aria-label={checkboxLabel}
              checked={selected}
              /* ADR-0162 D2.6 step 4 — THE CATALOG-LEAF GATE IS GONE from all
                 three of this input's props.

                 It read `checked={selected && !isCatalogLeaf}`,
                 `disabled={isCatalogLeaf || …}` and
                 `onClick={isCatalogLeaf ? undefined : onToggleSelect}`,
                 justified as: "ADR-0145 D7 — a catalog leaf cannot be confirmed
                 yet, and this checkbox is what feeds the bulk-confirm CTA.
                 Selectable-but-unconfirmable is the exact
                 present-and-dead-ended state D7 rules out."

                 THE REASONING WAS RIGHT AND IS WHY THE ORDER MATTERED. The
                 premise — "cannot be confirmed yet" — is what changed, and only
                 after the server could actually confirm: `confirmParts` now
                 takes the catalog arm as a parameter and migration 1021 records
                 which BOM position was confirmed. Opening this checkbox BEFORE
                 that would have produced precisely the dead end the old comment
                 names, which is why ADR-0162 D2.6 makes the sequence
                 merge-blocking rather than merely advisory.

                 THE CITATION WAS A PHANTOM, though the argument was not:
                 ADR-0145 D7 is "Tier and the authorization path" and refuses no
                 control (ADR-0162 D1.2 — one of seven such citations, all
                 reaching for ADR-0144 D7's PRINCIPLE).

                 STILL DISABLED WHEN IGNORED, and that clause is untouched: an
                 ignored row cannot be confirmed on either arm, which the
                 projection now derives for a catalog leaf too (D3.5(1)). So
                 this is not a blanket opening — the one condition that still
                 dead-ends a confirm still disables the control. */
              disabled={
                (props.kind === 'matched' && props.part.status === 'ignored') ||
                isUnactionableCatalogRow
              }
              onClick={onToggleSelect}
              onChange={() => {}}
            />
          </label>
        </td>
        <td>
          <PartThumbnail className="staging-row-thumb" url={props.part.thumbnailUrl} />
        </td>
        <td>
          {/* UID-525 / ADR-0168 — see the unresolved leaf's identical
              comment: no rail, and one flat 80px leading inset for every
              part row at any depth. */}
          <div className="staging-bom-name-cell staging-bom-leaf-name-cell">
            <div className="parts-row-name">
              <span
                className={
                  isIgnoredMatchedLeaf
                    ? 'parts-row-name-text staging-bom-leaf-name-ignored'
                    : 'parts-row-name-text'
                }
                title={packDisplay?.displayName ?? name}
              >
                {packDisplay?.displayName ?? name}
              </span>
              <span className="caption-text staging-bom-leaf-meta">
                {partNumberLabel && (
                  <>
                    <span className="staging-bom-leaf-part-number">{partNumberLabel}</span>
                    {/* C1 fix (code review) — a separator only when a segment
                      actually follows it; a loose leaf with no Line N text
                      must not leave " · " dangling.
                      Defect fix — `isCoverageGap` is REMOVED from this OR
                      chain along with the coverage-gap sentence it used to
                      precede (removed below): it no longer renders anything
                      here for this separator to follow.
                      UID-539 QA-gate fix — the drawing filename itself is no
                      longer appended to this meta line at all; it duplicated
                      the drawing chip already shown in the next column. */}
                    {(occurrenceCount > 1 || bomLine !== null) && (
                      <span aria-hidden="true"> · </span>
                    )}
                  </>
                )}
                {/* UX-spec §9.7 — reaffirmed, unchanged by §9.8 (only the
                  accessible-name copy above was superseded). Multiplicity
                  disclosure on the visible meta line: `occurrenceCount` can
                  only exceed 1 on a resolved leaf (unresolved leaves are
                  never deduped, D17.1), so this branch is the only one that
                  ever renders it.
                  Defect fix — the coverage-gap sentence ("Not referenced in
                  this BOM's structure") is REMOVED per product ruling.
                  `isCoverageGap` itself is untouched — see
                  `isUnactionableCatalogRow`'s doc comment above. */}
                {occurrenceCount > 1 ? (
                  <span>{formatOccurrenceMeta(bomLine, occurrenceCount)}</span>
                ) : (
                  bomLine !== null && <span>Line {bomLine}</span>
                )}
              </span>
            </div>
          </div>
        </td>
        <td className="staging-bom-drawing-cell">
          {props.kind === 'identified' ? (
            <DrawingAssociationControl
              partName={props.part.name}
              drawingFilename={props.part.drawingFilename}
              candidateDrawings={props.candidateDrawings}
              onAssociateDrawing={props.onAssociateDrawing}
              onGenerateDrawing={props.onGenerateDrawing}
              onUploadDrawing={props.onUploadDrawing}
              emptyLabel="No drawing"
              /* ADR-0144 D5 — `menuLabel` IS this control's accessible name,
                 and the menu it names contains associate / generate / upload
                 — all DRAWING verbs (`DrawingAssociationControl`'s whole prop
                 surface, and its own comment: "there is no separate
                 drawing-catalog lookup"). "Catalog match for X" therefore
                 told a screen-reader user the control does something it
                 cannot do: a live WCAG 2.2 4.1.2 (Name, Role, Value) failure.
                 Replaced with `DrawingAssociationControl`'s OWN documented
                 alternative (see its `menuLabel` doc comment).

                 This changes an accessible NAME, not the cell's structure:
                 UX-spec §4.1 requires ONE control with a
                 `(hasDrawing, isCatalogMatched, matchQuality)` discriminant
                 ("not a new component per state"), and the two-branch cell
                 here already satisfies that — §4.1 survives intact.

                 CORRECTED, ADR-0146 D1 / D11 step 3. This comment used to say
                 the catalog half (§4.1 state 2, "N similar parts") was
                 "blocked on R11 (a real catalog join)". That is NO LONGER
                 TRUE, in two ways, and ADR-0146 records both:
                   - the join exists (`catalog-leaf-sourcing.ts`), and
                   - the candidates were never missing. `resolveOne` already
                     searched with `limit: 10` and DISCARDED up to nine rows;
                     D11 step 3 stops discarding them, so a catalog leaf's
                     `sourcingOptions` now carries the exact hit plus its
                     non-exact candidates (`matchPercent: null`, D2).
                 The DATA for state 2 is therefore on the wire today. What is
                 still absent is the CONTROL: the trigger + disclosure panel is
                 ADR-0146 D11 **step 5**, and the durable select/keep verbs are
                 **step 4**. Until step 4 lands there is nothing to write, so
                 the control stays absent rather than present-and-dead-ended
                 (ADR-0144 D7) — the same reason as before, but now a WRITE-PATH
                 reason and not a missing-data one.
                 `emptyLabel="No drawing"` was already correct — left alone. */
              menuLabel={`Associated drawing for ${props.part.name}`}
              // UID-560 — this column ("Catalog match / drawing") wants a
              // consistent pill width per row, not shrink-to-fit; the column
              // already has a fixed width (`.staging-bom-col-match`).
              fullWidth
            />
          ) : (
            <MatchedCatalogMatchCell {...props} panelId={panelId} />
          )}
        </td>
        <td>
          {/* ADR-0136 §2 byproduct — the matched leaf's Part Type chip
              previously hardcoded `tone="info"`; every matched part IS a
              catalog part, so it must use the same `PART_TYPE_TONE.catalog`
              (success) tone the shipped Identified bucket uses for a
              `type: 'catalog'` part, not a second, inconsistent tone.

              UID-538 (T5) — the matched arm's OWN `statedPartType` now wins
              over the unconditional `'Buy'` fallback when the CSV stated
              one (`partTypeForChip` above). ADR-0149 Am.1 D16/D18 — render-
              time precedence only; `MatchedPart`/`part.type` are never
              overwritten. */}
          <Chip tone={PART_TYPE_TONE[partTypeForChip]}>
            {STAGED_PART_TYPE_LABEL[partTypeForChip]}
          </Chip>
        </td>
        {/* UID-525 / ADR-0168 — classed, not merely positional. Tests reached
            this cell by hopping `previousElementSibling` back from the Qty
            cell, which the new spacer column silently broke. A named hook
            survives the next column change too. */}
        <td className="staging-bom-status-cell">
          <ConfigurationStatusCell {...props} />
        </td>
        {/* UID-525 / ADR-0168 — the comp's `flex:1` spacer column
            (`dc.html:3161`), which pushes Qty and the delete action to the
            trailing edge and absorbs the table's slack. */}
        <td />
        <td className="staging-bom-qty-cell">
          {props.kind === 'identified' ? (
            // ADR-0156 INVARIANT D1.1 — the identified arm reads/writes
            // NEITHER node quantity column; `IdentifiedPart.quantity` stays
            // the unconditional source of truth (its part-scoped write also
            // keeps `configuration.quantity` → `indicativePrice` in step),
            // so `value`/`placeholder` are the SAME field and there is no
            // captured/desired split to affix an affordance to.
            <QuantityInput
              value={props.part.quantity}
              placeholder={props.part.quantity}
              onQuantityChange={props.onQuantityChange}
              ariaLabel={quantityLabel}
            />
          ) : (
            // ADR-0156 D1 — matched AND catalog both write through the
            // SAME node-scoped verb (F1: a matched leaf's rendered quantity
            // used to be a hard-coded `1` literal with no persistence seam
            // at all; a catalog leaf's was already node-sourced). Reads
            // `capturedQuantity`/`desiredQuantity` off the NODE, never
            // `part.quantity` (the flat array's fabricated/stale field).
            <QuantityInput
              value={props.desiredQuantity}
              placeholder={packDisplay ? packDisplay.displayQuantity : props.capturedQuantity}
              // UID-494 (T7) code-review C2 fix — the sr-only provenance text
              // must keep announcing the REAL sheet quantity even when
              // `placeholder` above is overridden to the packed `1`.
              capturedProvenanceValue={props.capturedQuantity}
              onQuantityChange={props.onNodeQuantityChange}
              ariaLabel={quantityLabel}
              showCapturedAffordance
            />
          )}
          {/* ADR-0156 R1 — a resolved leaf cited at N BOM positions is
              deduped to this ONE row (`occurrenceCount`, D17.1); an edit
              here writes through for all of them, which is not obvious
              from the number alone. Copy is a product-designer DRAFT
              pending PM sign-off — see the constant's own comment. */}
          {occurrenceCount > 1 && (
            <span className="caption-text staging-bom-qty-scope">
              {PENDING_R1_OCCURRENCE_QTY_COPY(occurrenceCount)}
            </span>
          )}
        </td>
        <td className="staging-bom-remove-cell">
          {/* ADR-0157 D1/D2 — Remove now renders on EVERY resolved leaf,
             catalog included: the design's own trash sits inside the
             generic `sc-if value="{{ r.isPart }}"` branch with no
             conditional of its own
             (`design_handoff_misumi_unified_platform/Fictiv Platform.dc.
             html:3237`), and there is no longer a structural reason to
             suppress it here (see the `isCatalogLeaf` doc comment above for
             the corrected record — the old `!isCatalogLeaf` gate and its
             ADR-0144 D7 "would 404" comment are both gone with this commit).
             `onRemoveCatalogNode` is the NEW node-scoped verb catalog leaves
             dispatch to; a real matched/identified leaf keeps the existing
             part-scoped `onRemove`. `removeLabel` is UNCHANGED — no
             "catalog" wording added (ADR-0146 D8's 4.1.2 lesson, ADR-0157's
             explicit instruction: same control, same treatment, no
             distinguishing). */}
          <button
            type="button"
            className="staging-row-remove"
            aria-label={removeLabel}
            onClick={
              props.kind === 'matched' && isCatalogLeaf ? props.onRemoveCatalogNode : props.onRemove
            }
          >
            <DeleteOutlined aria-hidden="true" />
          </button>
        </td>
      </tr>
      {props.kind === 'matched' && props.expanded && props.part.status !== 'ignored' && (
        // ADR-0146 D11 step 5 — the `!isCatalogLeaf` gate that used to
        // live here is REMOVED. It existed only because
        // `MatchedSourcingPanel` rendered `{option.matchPercent}%`, which
        // for a null candidate (D2) yielded a bare "%" — fixed FIRST, in
        // `MatchedSourcingPanel.tsx`, before this gate came off (the
        // ordering the old comment demanded). The panel now also omits
        // the Keep row for a catalog leaf (D4/OQ-C — no design state, no
        // approved copy) and writes through `onSelectCatalogOption`, not
        // `onSelectOption` (see `MatchedExpandedRow` below).
        <MatchedExpandedRow {...props} panelId={panelId} />
      )}
    </>
  )
}

/**
 * UX-spec §9.7 — visible meta-line copy for a deduped resolved leaf
 * (`occurrenceCount > 1`). Never fabricates a line number when `bomLine` is
 * null (an assembly-scoped leaf with no customer line number) — renders the
 * multiplicity-only sentence instead.
 */
function formatOccurrenceMeta(bomLine: number | null, occurrenceCount: number): string {
  const otherCount = occurrenceCount - 1
  const lineWord = otherCount === 1 ? 'line' : 'lines'
  return bomLine !== null
    ? `Line ${bomLine} (also on ${otherCount} other ${lineWord})`
    : `Also on ${otherCount} other ${lineWord}`
}

/** UID-493 — the comp's `pnLabel` rule verbatim (`Fictiv Platform.dc.html:9408`):
 * render a catalog leaf's part number only when it is a real value, hiding
 * em-dash/hyphen/"custom"/blank placeholders. `null` for a non-catalog
 * `partId` (a file-backed matched leaf has no catalog SKU to show). Exported
 * for direct unit coverage of the placeholder rule. */
export function catalogLeafPartNumberLabel(partId: string): string | null {
  const sku = catalogLeafSkuFromPartId(partId)
  if (sku === null) return null
  const trimmed = sku.trim()
  return ['—', '-', 'custom', ''].includes(trimmed.toLowerCase()) ? null : trimmed
}

/** UID-494 (T7) — verbatim transcription of the comp's `isPack` word list and
 * threshold (`Fictiv Platform.dc.html:9390`), which `demo-sourcing-generator.ts`'s
 * `PACKABLE_NAME`/`PACK_QUANTITY_THRESHOLD` already mirror server-side. Kept in
 * sync by hand (both are direct transcriptions of the same comp line) rather
 * than shared, since T2's fabricated `displayName`/`displayQuantity` never
 * reach the wire (see `packDisplay`'s own comment). */
const PACKABLE_NAME_PATTERN =
  /\b(?:screws?|washers?|nuts?|set ?screws?|standoffs?|dowels?|inserts?|rivets?|bolts?)\b/i
const TRAILING_PACK_LABEL = /\s*\(pack\)\s*$/i
const PACK_QUANTITY_THRESHOLD = 25

/** UID-494 (T7) — the comp's `isPack` collapse: a packable name at
 * `quantity >= 25` displays as qty 1 with a `· <quantity>-pack` suffix.
 * `null` when the heuristic does not fire (a `null` `quantity` — never
 * captured — trivially never reaches the threshold). */
export function packDisplayFor(
  name: string,
  quantity: number | null,
): { readonly displayName: string; readonly displayQuantity: number } | null {
  if (
    quantity === null ||
    quantity < PACK_QUANTITY_THRESHOLD ||
    !PACKABLE_NAME_PATTERN.test(name)
  ) {
    return null
  }
  return {
    displayName: `${name.replace(TRAILING_PACK_LABEL, '')} · ${quantity}-pack`,
    displayQuantity: 1,
  }
}

/**
 * UID-494 (T7) — the match-chip/state-chip/Swap-button state machine, all
 * three derived from the SAME two facts `stdRow` derives them from
 * (`Fictiv Platform.dc.html:9391/9400/9405`): whether the match is exact, and
 * whether a sourcing option is currently selected. `matchType` stays
 * `'exact'` even after a manual pick on an exact-match row (`selected` is the
 * only field a selection ever changes, see `applyNodeMatchDecisions`'s own
 * comment) — a non-exact SELECTED option is therefore the one and only signal
 * for "the user picked something", since a non-exact candidate never starts
 * `selected: true` on its own (`catalog-leaf-resolution.ts`).
 */
function matchStateForLeaf(part: MatchedPart): {
  readonly foundLabel: string
  readonly foundTone: ChipTone
  readonly swapLabel: string
  readonly needsSelection: boolean
} {
  const isExactMatch = part.matchType === 'exact'
  if (isExactMatch) {
    // Code-review S1 — a literal `false`, not `!some(selected)`. Today an
    // exact hit always carries a `selected: true` option by construction
    // (`catalog-leaf-resolution.ts`'s seeding, and the `'kept-bom-reference'`
    // fold re-selects rather than clearing), so the two are equivalent — but
    // the comp makes "exact ⇒ never needs a selection" true BY CONSTRUCTION
    // (`chosen` falls back to `opts[0]` whenever `hasExact`, `stdRow`
    // `:9386-9388`), and this should stay true even if that invariant ever
    // moved three modules away.
    return {
      foundLabel: 'Direct match',
      foundTone: 'success',
      swapLabel: 'Swap',
      needsSelection: false,
    }
  }
  const needsSelection = !part.sourcingOptions.some((option) => option.selected)
  if (!needsSelection) {
    return { foundLabel: 'Selected', foundTone: 'success', swapLabel: 'Change', needsSelection }
  }
  return { foundLabel: 'No exact match', foundTone: 'warning', swapLabel: 'Select', needsSelection }
}

function MatchedExpandedRow(props: MatchedLeafProps & { readonly panelId: string }) {
  const {
    part,
    onSelectOption,
    onSelectCatalogOption,
    onKeepUploaded,
    onKeepCatalogNode,
    panelId,
  } = props
  // ADR-0146 D1/D6b + ADR-0162 D5 — a catalog leaf writes through the
  // NODE-subject verbs (`onSelectCatalogOption`, `onKeepCatalogNode`); a
  // file-backed leaf keeps the `partId`-scoped pair and is byte-unchanged.
  //
  // THE KEEP ROW NOW RENDERS ON BOTH ARMS. This comment used to say a catalog
  // leaf "never shows the Keep row (no design state for it yet, OQ-C)",
  // citing ADR-0146 D4. That premise is FALSIFIED by the authoritative design:
  // `stdRow` (`Fictiv Platform.dc.html:9384`) sets `onKeep`/`keepSel`/`kept`
  // and the Keep row (`:3242`) renders inside the `bomRows` loop (`:3165`) —
  // i.e. on precisely this population. Only the WRITE PATH differs between the
  // arms, which is what the ternaries below now express.
  //
  // The COPY, however, is NOT the design's literal string on the catalog arm:
  // ADR-0146 Am.1 D12 closed OQ-C by human ruling with catalog-accurate copy,
  // superseding ADR-0162 R1's "ship the design's wording, OQ-C stays open".
  // That is why the catalog arm below routes to `onKeepBomReference` (which
  // carries the ruled copy) rather than `onKeepUploaded` (which carries the
  // D4-forbidden upload wording) — see that prop's own comment.
  const isCatalogLeaf = isCatalogLeafPartId(part.partId)
  // Rendered as a SIBLING <tr> of the leaf row (the parent returns a fragment
  // of both), never nested inside it — a <tr> inside a <tr> is invalid table
  // markup the browser silently reparents. It spans every column, mirroring
  // the table-based expansion `MatchedPartRow` already uses in the flat
  // fallback. No `aria-level` (ADR-0136 D5 correction — see the leaf row's
  // own `<tr>` comment: invalid on a plain-table row per axe-core).
  //
  // Defect fix (2026-09-09) — this was `colSpan={7}`, stale since ADR-0136
  // (commit 47154c9c) added the 8th column (`ConfigurationStatusChip`'s own
  // `<td>`, between Part type and Qty) without bumping this span, leaving the
  // panel one column short of full width so it no longer lined up under the
  // triggering row per `Fictiv Platform.dc.html:3240`.
  //
  // UID-525 / ADR-0168 — bumped again, 8 -> 9: that ADR adds the comp's
  // `flex:1` spacer column (`dc.html:3161`) between Configuration status and
  // Qty. THIS SPAN IS A STANDING TRAP — it is a literal that has now gone
  // stale twice, on both occasions because a column was added elsewhere. It
  // is guarded by `StagingBomLeafRow.adr-0149-am1-d19.test.tsx`'s "the
  // sourcing-panel <td> spans exactly as many columns as the matched leaf row
  // has", which derives the expected value from the row itself rather than
  // restating the literal — that test is what caught this bump.
  return (
    <tr className="staging-bom-row-expanded">
      <td colSpan={9}>
        <MatchedSourcingPanel
          id={panelId}
          part={part}
          onSelectOption={isCatalogLeaf ? onSelectCatalogOption : onSelectOption}
          onKeepUploaded={isCatalogLeaf ? undefined : onKeepUploaded}
          /* ADR-0146 Am.1 D12 (copy) x ADR-0162 D5 (write path) — the catalog
             arm's keep row. MERGE RECONCILIATION: `main` wired this arm through
             `onKeepUploaded`, which carries the D4-forbidden upload wording;
             Am.1 D12 is the later human ruling and closed OQ-C with
             catalog-accurate copy, so the row renders through
             `onKeepBomReference` instead. The VERB is `main`'s node-subject
             `onKeepCatalogNode` either way — only the copy differs, and only
             the copy was in dispute. Mutually exclusive with `onKeepUploaded`
             above by the same discriminant, so exactly one keep row can ever
             render. */
          onKeepBomReference={isCatalogLeaf ? onKeepCatalogNode : undefined}
          // UID-494 (T7) design-parity gap review — this row is ALWAYS the
          // BOM-tree surface (`bomRows`' expansion), never the flat
          // `matchedRows` one, regardless of whether the leaf is a catalog
          // leaf or file-backed. See `MatchedSourcingPanel`'s own `variant`
          // doc comment for what this suppresses/adds. SUPERSEDES this
          // branch's `showSearch={false}`: both mechanisms suppressed the same
          // in-panel search box, and `variant` is the one that also carries the
          // design's left accent bar.
          variant="bom-tree"
        />
      </td>
    </tr>
  )
}

/**
 * ADR-0156 R1 — the resolved-leaf-cited-at-N-positions Qty-cell disclosure.
 * PRODUCT-DESIGNER DRAFT COPY, PM sign-off PENDING (ADR-0156 routes final R1
 * wording to PM, not yet obtained) — kept as a single named function so
 * swapping the string is a one-line change and a reviewer can see it is
 * provisional, not settled house copy like `buildLeafAccessibleName`'s.
 */
function PENDING_R1_OCCURRENCE_QTY_COPY(occurrenceCount: number): string {
  return `Applies to all ${occurrenceCount} lines`
}

/**
 * ADR-0156 D1/D4/R5 — the ONE Qty input every leaf row now renders (the
 * canonical design's `<input value="{{ r.qtyStr }}" inputmode="numeric">`,
 * one template, no per-arm variant). Server-authoritative, no optimistic
 * update (D4): a draft is held locally and committed on blur/submit.
 *
 * `value`/`placeholder` are two DIFFERENT numbers only on the
 * matched/catalog/unresolved arms (`desiredQuantity`/`capturedQuantity`);
 * the identified arm passes the SAME field for both (D1.1 — no split
 * exists there), which degenerates this component back to its pre-ADR-0156
 * shape for that arm with zero behavior change.
 *
 * `showCapturedAffordance` gates the R5 placeholder-vs-value treatment
 * (the `aria-describedby` sr-only description plus the `-captured`/
 * `-desired` CSS modifiers) so the identified arm's appearance stays
 * byte-identical — it has no captured/desired distinction to render.
 *
 * REVERT-ON-REJECTION (D4, security-review Gate-5 fix): `draft` is seeded
 * from `value` on mount and otherwise never re-synced from props — rows are
 * keyed by the stable `nodeId`, so a prop change alone never remounts this
 * input, and `value`/`placeholder` DON'T CHANGE on a rejected edit (the
 * mutation throws before the page's triage state is replaced), so a plain
 * `useEffect` keyed on those props could never fire for the one case that
 * matters. Instead `commit()` itself `await`s `onQuantityChange`'s own
 * `true`/`false` result (never a throw — `StagingPage`'s `runMutation`
 * already surfaces the error to the page-level alert; this component isn't
 * where that error is displayed) and reverts the draft ONLY on `false`.
 *
 * The revert is guarded against stomping the user's NEXT keystroke: it
 * captures the exact draft string this commit attempt is reverting (
 * `draftAtCommit`) and only writes the reverted value back via a functional
 * `setDraft` that checks the CURRENT state still equals `draftAtCommit`. If
 * the user already refocused and typed something else while the (async,
 * network-bound) commit was in flight, the revert is a no-op — it never
 * clobbers live, in-progress typing with a stale rejection.
 */
function QuantityInput({
  value,
  placeholder,
  capturedProvenanceValue = placeholder,
  onQuantityChange,
  ariaLabel,
  showCapturedAffordance = false,
  disabled = false,
}: {
  /** The WORKING value to render solid, or `null` to render only the
   * placeholder (R5 — a captured quantity MUST NOT render as the input's
   * filled value). */
  readonly value: number | null
  /** The captured fallback shown as the input's placeholder when `value`
   * is `null`; `null` renders no placeholder (nothing captured either). */
  readonly placeholder: number | null
  /**
   * UID-494 (T7) code-review C2 fix — the TRUE captured-off-the-sheet
   * number for the sr-only "Captured from the uploaded file: N" provenance
   * text, when it must read DIFFERENTLY from `placeholder` (which also
   * drives the DISPLAYED effective value). The one caller where these
   * diverge is a packed leaf: `placeholder` is deliberately overridden to
   * `1` so the field displays "1" per the comp's pack collapse, but telling
   * a screen-reader user the sheet captured "1" would be false — it
   * captured the real pre-pack quantity. Defaults to `placeholder` (the
   * pre-existing behavior) for every other caller, so this is additive.
   */
  readonly capturedProvenanceValue?: number | null
  /** ADR-0156 D4 — resolves `true` on a successful commit, `false` on a
   * rejected one. MUST NOT throw/reject: the caller (`StagingPage`) already
   * catches and surfaces the failure via its own `runMutation`/
   * `mutationError` alert; this boolean is purely the revert signal. */
  readonly onQuantityChange: (quantity: number) => Promise<boolean>
  readonly ariaLabel: string
  readonly showCapturedAffordance?: boolean
  /** ADR-0157 D3 — a dismissed unresolved leaf disables (never hides) this
   * input: the native `disabled` attribute is enough to stop edits while
   * leaving the `value`/`placeholder`/`aria-describedby` sr-only description
   * all still rendering and announced (a `disabled` input still exposes its
   * `value`/placeholder to assistive tech — hiding the node would not). */
  readonly disabled?: boolean
}) {
  // HUMAN RULING (2026-09-07), resolving ADR-0156 R5 and REVERSING D2's
  // "MUST NOT render a captured quantity as a filled input value" —
  // `effectiveValue` is what the field actually shows: the human's
  // `desiredQuantity` when there is one, otherwise the number captured off
  // the sheet, rendered SOLID exactly as the design does
  // (`Fictiv Platform.dc.html:3235` — `value="{{ r.qtyStr }}"`, re-measured
  // off the running prototype at 13px/600 in `--fg-1`, never a placeholder).
  //
  // `sumGroupQuantity` moved with it (see its own comment): an assembly's
  // `Qty = N` now counts a captured quantity too, so a leaf showing a solid
  // `4` is never excluded from the total above it.
  const effectiveValue = value ?? placeholder
  const [draft, setDraft] = useState(effectiveValue !== null ? String(effectiveValue) : '')
  const inputId = useId()
  const descriptionId = useId()
  // Still tracked, but now ONLY to decide the sr-only provenance description
  // below ("Captured from the uploaded file…") — the number is on screen
  // either way, so what a captured-but-unconfirmed value still owes the user
  // is the fact that it came off their sheet rather than from them. It no
  // longer gates any visual placeholder/value treatment, because there is no
  // longer a placeholder to gate.
  // UID-494 (T7) code-review C2 fix — keyed on `capturedProvenanceValue`
  // (the TRUE captured number), not `placeholder` (which may be the packed
  // override `1`): "was anything genuinely captured off the sheet" is the
  // real question this affordance answers.
  const isCaptured = showCapturedAffordance && value === null && capturedProvenanceValue !== null

  async function commit() {
    // Gate-5 fix (code-review suggestion) — floor BEFORE the `!== value`
    // comparison, not after: with `value === 2`, typing "2.5" used to
    // compare the un-floored `2.5 !== 2` (true), fire a no-op PATCH of
    // `Math.floor(2.5) === 2`, and await a round trip for a value that was
    // never going to change anything.
    const parsed = Math.floor(Number(draft))
    const draftAtCommit = draft
    // Reverts to `effectiveValue`, NOT `value` — with the captured number now
    // rendered solid, `value` is still `null` on an un-edited leaf, so
    // reverting to it would BLANK a field that was visibly showing `4` before
    // the user's invalid keystroke. Restores what was on screen.
    const revertToWhatWasShowing = effectiveValue !== null ? String(effectiveValue) : ''
    // `parsed !== value` (not `!== effectiveValue`) is deliberate: re-typing
    // the captured number IS a real change — it promotes a value that was
    // merely read off the sheet into one the human has confirmed
    // (`desiredQuantity`), which is exactly what the rollup now counts as
    // theirs. Comparing against `effectiveValue` would swallow that as a
    // no-op and leave the quantity permanently unconfirmed.
    if (draft.trim() !== '' && Number.isFinite(parsed) && parsed >= 1 && parsed !== value) {
      const accepted = await onQuantityChange(parsed)
      if (!accepted) {
        // Refused (409/404/400/network failure) — snap back to the
        // authoritative value, UNLESS the user has already moved on and
        // typed something new while this commit was in flight, in which
        // case leave their new draft alone.
        setDraft((current) => (current === draftAtCommit ? revertToWhatWasShowing : current))
      }
      return
    }
    // Invalid, empty, or unchanged — revert to whatever this input was
    // already showing.
    setDraft(revertToWhatWasShowing)
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    void commit()
  }

  return (
    <form onSubmit={handleSubmit}>
      <label className="sr-only" htmlFor={inputId}>
        {ariaLabel}
      </label>
      {/* A TEXT input with `inputMode="numeric"`, matching the design exactly
          — re-measured off the running prototype, its Qty control has NO
          `type` attribute at all (so, text) plus `inputmode="numeric"`, and
          no `min`/`max`/`step`/`pattern`.

          This was `type="number" min={1}`, which is not a cosmetic difference
          — it hands the browser three behaviours the design does not have,
          one of them a real data-corruption hazard:
            1. MOUSE WHEEL over a focused number input silently increments or
               decrements it. On a long BOM the user scrolls the list with the
               cursor sitting over a Qty they just focused, and quantities
               change under them with no interaction they'd recognise as an
               edit. `commit()` on blur then PATCHes the scrolled value.
            2. ARROW UP/DOWN likewise step the value — stepper semantics on a
               control the design gives no stepper.
            3. `min={1}` drives native validation UI (`:invalid` styling and
               a browser tooltip) that the design never shows; the real
               minimum is already enforced in `commit()` (`parsed >= 1`),
               which is where it belongs since it must hold for typed input
               too.
          Dropping `type="number"` removes all three. `inputMode="numeric"`
          keeps the mobile numeric keypad, which is the only part of
          `type="number"` this control actually wanted.

          NOTE the a11y role changes from `spinbutton` to `textbox`. That does
          NOT reopen ADR-0156 D6 (the "N identical Quantity spin buttons"
          WCAG 4.1.2 failure): D6 is about each control having a UNIQUE
          ACCESSIBLE NAME, which `buildLeafAccessibleName` still supplies
          verbatim — the role was never what satisfied it. */}
      {/* No `placeholder` attribute and no `-captured`/`-desired` modifier
          classes any more: both existed ONLY to render the captured-but-
          unconfirmed state as greyed placeholder text, which the 2026-09-07
          ruling replaces with a solid value. There is now ONE visual
          treatment for this field in every state — which is what the design
          has (`:3235` carries no placeholder and one style). The provenance
          of a captured value is still conveyed, but by the sr-only
          `aria-describedby` node below rather than by colour/weight. */}
      <input
        id={inputId}
        inputMode="numeric"
        className="staging-qty-input staging-bom-qty-input"
        value={draft}
        aria-describedby={isCaptured ? descriptionId : undefined}
        disabled={disabled}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
      />
      {/* ADR-0156 R5 — a `placeholder` attribute alone is NOT the field's
          accessible value (most screen readers announce an empty `value`
          as blank, never reading the placeholder) — the same class of gap
          as the live 4.1.2 failure ADR-0146 D8 records. This sr-only node,
          wired via `aria-describedby`, is what actually announces the
          captured number. Dropped entirely once edited — the real `value`
          then self-describes and no description is needed. */}
      {isCaptured && (
        <span id={descriptionId} className="sr-only">
          Captured from the uploaded file: {capturedProvenanceValue}. Not yet confirmed — enter a
          quantity to set it.
        </span>
      )}
    </form>
  )
}

/**
 * ADR-0136 §2 — replaces the deleted `ConfigurationStatusLabel`. That
 * component rendered DRAWING-presence facts for identified leaves and
 * SOURCING-selection facts for matched leaves under a header
 * ("Configuration status") that means something else entirely in the
 * shipped `IdentifiedPartRow` (`part.configuration !== null`) — one label,
 * two unrelated facts, and a duplicate of the drawing-presence fact the
 * leaf's own Catalog-match/drawing cell already shows one column over.
 *
 * The MATCHED branch renders the SAME `Chip` COMPONENT the corresponding
 * flat bucket row (`MatchedPartRow`'s `MATCH_TYPE_LABEL`/`MATCH_TYPE_TONE`)
 * renders for this same part.
 *
 * STALE-CLAIM CORRECTION (UID-494 T7, code-review W1) — this used to also
 * say "same label strings, same field", which stopped being true the moment
 * T7 gave the MATCHED branch its own `matchStateForLeaf` derivation
 * ("Direct match"/"Selected"/"No exact match", `stdRow`'s `foundLabel`,
 * `Fictiv Platform.dc.html:9400`) instead of the flat row's
 * `MATCH_TYPE_LABEL`/`MATCH_TYPE_TONE` ("Exact match"/"Close match"/etc,
 * `matchedRows`' own `matchTypeLabel`, `:3348`). The comp actually gives
 * BOTH surfaces the SAME vocabulary (`:3348` mirrors `:9400` field-for-
 * field) — `MatchedPartRow.tsx` is the one left on the OLD table, a real
 * cross-surface label fork the comp does not call for. Flagged for the
 * PM/product-designer (W1): fixing it means editing `MatchedPartRow.tsx`,
 * a different comp surface outside this ticket's stated file scope
 * (`apps/web/.../StagingBomLeafRow.tsx`) — not silently forked here, but not
 * corrected here either.
 *
 * STALE-CLAIM CORRECTION (UID-538 T5, superseded again by a product-ruled
 * follow-up) — the IDENTIFIED branch used to share `IdentifiedPartRow`'s
 * "Configured"/"Not configured" `Chip` exactly; it no longer does. Renamed
 * from `ConfigurationStatusChip` accordingly — this branch now delegates to
 * `ConfigurationStatus` (below), which CAN render one or two `Chip`s, or
 * nothing, depending on whether a stated config and a drawing each exist.
 * See that component's own doc comment and ADR-0149 Am.8 D49.
 */
/**
 * Product-ruled follow-up (post-UID-538) — the Make row's Configuration
 * status cell names whichever of the two independent facts a human still
 * needs to act on is MISSING (no stated Process/Material, no drawing
 * attached), rather than restating the CSV's `Process · Material` text
 * (T5's original AC, now superseded by this ruling on both arms). The
 * cell is blank once both are present — there is nothing left to flag,
 * and the drawing itself is already shown one column over.
 */
function ConfigurationStatus(props: { readonly hasConfig: boolean; readonly hasDrawing: boolean }) {
  const { hasConfig, hasDrawing } = props
  if (hasConfig && hasDrawing) return null
  if (!hasConfig && hasDrawing) return <Chip tone="neutral">No configuration</Chip>
  if (hasConfig && !hasDrawing) return <Chip tone="neutral">No drawing</Chip>
  return (
    <div className="staging-bom-config-status-stack">
      <Chip tone="neutral">No configuration</Chip>
      <Chip tone="neutral">No drawing</Chip>
    </div>
  )
}

function ConfigurationStatusCell(props: IdentifiedLeafProps | MatchedLeafProps) {
  if (props.kind === 'matched') {
    if (props.part.status === 'ignored') return <Chip tone="warning">Ignored</Chip>
    const { foundLabel, foundTone } = matchStateForLeaf(props.part)
    return <Chip tone={foundTone}>{foundLabel}</Chip>
  }
  // UID-538 (T5) — the identified arm's leaves are BOM lines, not
  // `staged_file` rows a human ever configures via `PATCH parts/:partId`
  // (ADR-0124 D1 — no Configure button on Triage); `part.configuration`
  // therefore never meant anything here and is dropped.
  const hasConfig = [props.statedProcess, props.statedMaterial].filter(Boolean).length > 0
  return (
    <ConfigurationStatus hasConfig={hasConfig} hasDrawing={Boolean(props.part.drawingFilename)} />
  )
}

/**
 * ADR-0146 D8 — the accessible name for the catalog-match disclosure
 * trigger, shared by BOTH the file-backed and the catalog-leaf branch below
 * (item 3: "reuse the existing pattern, corrected for D2 and for the
 * `n === 1` case").
 *
 * The `n === 1` case is handled FOR FREE, not by a separate branch: a
 * catalog leaf's exact hit carries `selected: true` by default (D1) and
 * stays selected until a human picks a candidate, so `selectedOption` is
 * ALWAYS defined for a catalog leaf — the identity branch fires, and the
 * "N similar parts" wording (which would be false advertising for a single-
 * option row promising nothing to swap to) is never reached for that case.
 * A file-backed leaf with a live, not-yet-chosen candidate list is the one
 * population that can still hit the second branch.
 *
 * UID-510 — `part.matchedCatalogItem` is the BOM's OWN stated reference
 * (`name (catalogSku)`), frozen at match time and never touched by a swap or
 * a catalog-leaf select (`applySwap`/`applyDecision` only ever flip
 * `sourcingOptions[].selected`). Naming it here silently kept announcing the
 * pre-selection vendor after a human picked a different one. `selectedOption`
 * itself is the one fact that DOES change on a pick, so it — not the frozen
 * field — is what must be read for "what is currently chosen", the same
 * `vendor · sku` shape `MatchedPartRow`'s caption already uses for this exact
 * fact in the flat Matched-parts list.
 */
/**
 * UID-494 (T7) code-review C1 fix — `swapLabel` is now APPENDED, not a
 * separate parameter callers can omit: the trigger renders it as VISIBLE
 * text (`MatchedCatalogMatchCell`'s `.staging-bom-match-swap-label` span),
 * and an `aria-label` that omits visible text is WCAG 2.5.3 (Label in
 * Name) — a speech-input user saying "click Select" could not operate a
 * control accessibly named only "Catalog match for X: 3 similar parts".
 */
function catalogMatchAccessibleName(
  part: MatchedPart,
  selectedOption: SourcingOption | undefined,
  swapLabel: string,
): string {
  const summary = selectedOption
    ? `${selectedOption.vendor} · ${selectedOption.sku}`
    : `${part.sourcingOptions.length} similar part${part.sourcingOptions.length === 1 ? '' : 's'}, not yet selected`
  return `Catalog match for ${part.name}: ${summary}. ${swapLabel}.`
}

function MatchedCatalogMatchCell({
  part,
  expanded,
  onToggleExpand,
  onUndo,
  onUndoIgnoreCatalogNode,
  panelId,
}: MatchedLeafProps & { readonly panelId: string }) {
  // ADR-0162 D3.2 — HOISTED above the ignored branch, which it did not used to
  // be. That mattered: the branch below renders Undo for BOTH arms, and it wired
  // the `partId`-scoped `onUndo` unconditionally. Now that a catalog leaf can
  // actually BE ignored, leaving it that way would have made its Undo call
  // `confirmMatch` against a `staged_file` row that does not exist — a 404 on
  // the only route out of the ignored state, i.e. the dead end ADR-0144 D7's
  // principle rules out, reached by way of a control we had just opened.
  const isCatalogLeaf = isCatalogLeafPartId(part.partId)
  const isIgnored = part.status === 'ignored'
  if (isIgnored) {
    return (
      <div className="staging-bom-matched-cell">
        <Chip tone="warning">Ignored</Chip>
        {/* ADR-0157 — live AA-contrast fix (`.staging-drawing-trigger-none`
            alone paints this label at `--color-text-tertiary`, 3.35:1,
            below AA for text): the scoped override class, applied here
            while the file is open per the task's own instruction. */}
        {/* KEYED, and so is its counterpart in the active branch below — this
            is the SAME control in both, and the key is what says so.

            Focus depends on it. Activating Ignore swaps this cell from the
            active branch to this one, and a keyboard user's focus survives only
            if React REUSES the button's DOM node instead of remounting it.
            Until the origin/main @3d07d87d merge that reuse was an ACCIDENT of
            position: both branches happened to render their button as the
            second child, so the index-matching pass paired them. UID-493 then
            inserted a conditional price `<span>` ahead of the active branch's
            button, moving it to index 2 — and focus silently fell to
            `document.body`. Caught only because
            `StagingPage.bom-hierarchy.test.tsx`'s Gate-5 W7 characterization
            test pins it, exactly the "extra wrapper / different element type /
            keyed remount" restructuring that test warned would break it.

            A matching key makes the pairing explicit and position-independent:
            React's array reconciler falls back to a key lookup once the index
            pass mismatches, so the two buttons pair no matter what siblings are
            added ahead of either one. Do not remove either key without moving
            focus management somewhere explicit. */}
        <button
          key="staging-bom-match-action"
          type="button"
          className="staging-drawing-trigger staging-drawing-trigger-none staging-bom-dismiss-action"
          onClick={isCatalogLeaf ? onUndoIgnoreCatalogNode : onUndo}
        >
          Undo
        </button>
      </div>
    )
  }
  const selectedOption = part.sourcingOptions.find((option) => option.selected)
  /**
   * Gate-5 W4 — this read `staging-drawing-trigger-missing`, a class UID-402
   * (ADR-0124 D5) DELETED from `uploads.css`, leaving a dangling reference that
   * rendered an unstyled trigger (base rule only, no border/background). Routed
   * to `-none` deliberately, not as a cosmetic patch: the state here is "no
   * sourcing option selected yet", which the analysis signal classifies as
   * `ambiguous-match` — NOT `needs-resolution` — and D5 reserves the amber
   * treatment for needs-resolution alone. So the neutral/dashed treatment is the
   * semantically correct destination, and re-adding an amber rule for it would
   * reintroduce exactly the over-broad orange D5 removed.
   *
   * ADR-0136 re-homed this component onto `StagingPage`'s fourth "Assembly
   * hierarchy" section, so this is no longer latent/parked behaviour — it is
   * live the moment a session has a captured `bomHierarchy`.
   */
  if (isCatalogLeaf && part.sourcingOptions.length === 0) {
    /**
     * ADR-0145 D6, UNCHANGED by ADR-0146 (D1 ruling 4 reaffirms it): a
     * CONFIGURABLE FAMILY carries NO candidates — `sourcingOptions: []` — and
     * gets identity only, no affordance, no explanatory copy. D1's fix is
     * for the CONCRETE arm alone; a family is not a priceable point (ADR-0096
     * §1: no `min`/`max`/`step` bounds exist outside a credentials-gated PIM
     * endpoint), so there is genuinely nothing behind an expand here — not a
     * missing write path (D11 step 4 fixed that for the concrete arm), a
     * missing LIST. An expander that opens onto zero rows is the
     * present-and-dead-ended state ADR-0144 D7 rules out, so this stays a
     * plain readout, not a disabled button and not a "bounds unavailable"
     * notice (ADR-0142 D4/D4.1's silent posture).
     */
    return (
      <div className="staging-bom-matched-cell">
        <span className="staging-bom-matched-identity" title={part.matchedCatalogItem}>
          {part.matchedCatalogItem}
        </span>
      </div>
    )
  }
  // UID-494 (T7) — the `swapLabel` half of `matchStateForLeaf`; `needsSelection`
  // there is recomputed from the SAME `selected` flag `selectedOption` above
  // already reads, so the two never disagree.
  const { swapLabel, needsSelection } = matchStateForLeaf(part)
  // DESIGN-CONFORMITY CALL (UID-494 T7) — the ticket's prose describes a
  // two-line match chip ("label" + a "sub-line" reading the sku, or "Select
  // one to continue"). `stdRow` DOES compute that second line
  // (`matchChipSub`, `Fictiv Platform.dc.html:9403`) but NO template in the
  // comp ever reads `r.matchChipSub`/`m.matchChipSub` — the only markup for
  // this control (`:3223`) renders `matchChipLabel` + `swapLabel` + the
  // chevron on ONE line and nothing else (grep-verified: `matchChipSub`
  // appears exactly once in the whole file, its own assignment). Per this
  // file's own precedent for resolving a prose/markup disagreement (verify
  // against the actual rendering, not the data-model comment — see
  // CLAUDE.md's citation of the assembly-header case), a computed-but-never-
  // rendered field means the comp does not show it, full stop. No sub-line
  // is rendered here; the sku/"not yet selected" fact still reaches the
  // trigger's accessible name via `catalogMatchAccessibleName` below.
  return (
    <div className="staging-bom-matched-cell">
      {/* ADR-0146 D8 ruling 1 — `aria-haspopup="menu"` is REMOVED from BOTH
          branches of this trigger. It promised `role="menu"` semantics
          `MatchedSourcingPanel` (a `<table>` of native buttons) never had —
          a live WCAG 4.1.2 defect ADR-0146 found while building this
          control, not one it introduces. Disclosure semantics
          (`aria-expanded` + `aria-controls`) replace it: a native `<button>`
          plus a panel it points at by id, which is what this control
          actually is. */}
      {/* KEYED — human-review fix (visual): the comp's chosen/default state
          (`stdRow`'s `matchChipStyle`, `Fictiv Platform.dc.html:9404`) has NO
          background tint at all, just the base white surface + default
          border; `.staging-drawing-trigger-has`'s success-green tint (a
          DRAWING-trigger convention, `drawChipStyle`) was being applied here
          too and never belonged on this control. The needsSelection modifier
          below is therefore ADDITIVE to the plain base class only — no
          `-has`/`-none` tone class at all — which also fixes the amber
          state's border rendering DASHED: that came from `-none`'s own
          `border-style`, never applied by the comp's `matchChipStyle`
          (`border:1px solid #F0811E`, solid). Same `key` as the ignored
          branch's Undo button above (both buttons occupy this cell's first
          slot), so React reuses the DOM node — and keyboard focus — across an
          ignored<->active transition (Undo click here, or the flat Matched-
          bucket row's own Ignore button acting on the same part elsewhere). */}
      <button
        key="staging-bom-match-action"
        type="button"
        // UID-569 — `-fill` is UID-560's existing modifier, reused rather than
        // duplicated: this column's OTHER pill (the drawing branch of the same
        // `<td>`) already fills, and shipping two fill mechanisms for one
        // column is how they drift apart again.
        // `staging-bom-match-trigger` carries the comp's own gap/padding
        // (`Fictiv Platform.dc.html:9356`/`:9404`) as a scoped variant, so the
        // shared `.staging-drawing-trigger` base — also worn by the ignored
        // row's Undo button in this very cell — keeps its defaults.
        className={
          needsSelection
            ? 'staging-drawing-trigger staging-drawing-trigger-fill staging-bom-match-trigger staging-bom-match-trigger-needs-selection'
            : 'staging-drawing-trigger staging-drawing-trigger-fill staging-bom-match-trigger'
        }
        aria-expanded={expanded}
        aria-controls={panelId}
        aria-label={catalogMatchAccessibleName(part, selectedOption, swapLabel)}
        onClick={onToggleExpand}
      >
        <SearchOutlined aria-hidden="true" />
        <span className="staging-bom-match-label-text">
          {selectedOption
            ? `${selectedOption.vendor} · ${selectedOption.sku}`
            : `${part.sourcingOptions.length} similar part${part.sourcingOptions.length === 1 ? '' : 's'}`}
        </span>
        {/* UID-494 (T7) — the comp's `swapLabel` (`Fictiv Platform.dc.html:9405`):
            Swap/Change/Select, rendered inside the SAME trigger the design puts
            it in, ahead of the chevron. Also folded into the aria-label above
            (code-review C1 — WCAG 2.5.3, visible text must be part of the
            accessible name). */}
        <span className="staging-bom-match-swap-label">{swapLabel}</span>
        <DownOutlined aria-hidden="true" />
      </button>
      {/* UID-526 — pricing must not appear at BOM upload/triage stage; that's
          the quote/RFQ step's job, not this one. */}
      {/* UID-494 (T7) — REVERSES ADR-0162 D3.2's ruling to render this
          button. Human visual review against a live comp screenshot found
          NO Ignore control anywhere in this cell (checked both the
          direct-match and no-exact-match rows) — ADR-0162 D3.2's citation of
          `stdRow`'s `onIgnore` (`Fictiv Platform.dc.html:9425`) is real, but
          the comp's OWN rendered screen for this exact row never wires a
          button to it; `onIgnore` is dead data on this population, same
          category of gap CLAUDE.md's item 8 already names for a mapper field
          nothing consumes.

          `onIgnore`/`onIgnoreCatalogNode` stay wired PROPS — only the button
          that fired them from THIS cell is gone. A file-backed matched part
          still has a live Ignore entry point via the flat Matched-bucket row
          (`MatchedPartRow.tsx`, untouched, a different comp surface this
          ticket does not touch), and this tree row must still render/allow-
          undoing that state when it happens (the branch above, unchanged). A
          catalog leaf (no flat-bucket presence at all) now has NO way to
          become ignored, which matches the comp showing no ignore affordance
          anywhere on that population either. */}
    </div>
  )
}
